import React from "react";

import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { t } from "@impero-com/common/utils/i18n";
import { FormField, FormLabel } from "@impero-com/ui";
import { Typography } from "antd";
import { cx } from "class-variance-authority";
import { RefCallBack } from "react-hook-form";

import {
    ControlProgramTagCategoryView,
    NewControlProgramTag,
} from "api/generated/model";
import { EditableBaseTagListPlus } from "components/tag/editableBaseTagListPlus/editableTagListPlus";
import { ControlProgramTag } from "components/tag/editableBaseTagListPlus/types";
import {
    createNewControlProgramTag,
    getControlProgramTagCategoryId,
    isControlProgramTagActive,
} from "components/tag/editableBaseTagListPlus/utils";
import {
    ControlProgramTagsQuerySelect,
    useControlProgramTagsQuery,
} from "org/tags/controlProgramTags/queries/useControlProgramTagsQuery";
import { useCreateControlProgramTagMutation } from "org/tags/controlProgramTags/queries/useCreateControlProgramTagMutation";
import { Guid } from "utils/guid";

import { EditableTagListInputSkeleton } from "../../../../../components/form/skeleton/editableTagListInputSkeleton";

const { Text } = Typography;

export interface EditableControlProgramTagListInputBaseProps {
    disabled?: boolean;
    label?: React.ReactNode;
    canCreateTag?: boolean;
    cascaderClassName?: string;
    initialTags?: ControlProgramTagsQuerySelect;
}

interface EditableControlProgramTagListPlusInputProps extends EditableControlProgramTagListInputBaseProps {
    error?: string | undefined;
    selectedTagIds: Guid[];
    setSelectedTagIds: (tagIds: Guid[]) => void;
    inputRef?: RefCallBack;
    onBlur?: () => void;
}
export function EditableControlProgramTagListPlusInput({
    setSelectedTagIds,
    cascaderClassName,
    error,
    onBlur,
    inputRef,
    label,
    selectedTagIds,
    canCreateTag = false,
    disabled = false,
    initialTags,
}: EditableControlProgramTagListPlusInputProps) {
    return (
        <FormField
            fullWidth
            error={error}
            className={cx(error && ERRORED_CLASSNAME)}
        >
            {label && <FormLabel disabled={disabled}>{label}</FormLabel>}
            <EditableControlProgramTagListPlusContent
                setSelectedTagIds={setSelectedTagIds}
                selectedTagIds={selectedTagIds}
                initialTags={initialTags}
                cascaderClassName={cascaderClassName}
                onBlur={onBlur}
                inputRef={inputRef}
                canCreateTag={canCreateTag}
                disabled={disabled}
            />
        </FormField>
    );
}
function EditableControlProgramTagListPlusContent({
    setSelectedTagIds,
    selectedTagIds,
    initialTags,
    cascaderClassName,
    onBlur,
    inputRef,
    canCreateTag = false,
    disabled = false,
}: EditableControlProgramTagListPlusInputProps) {
    const showNoTagsLabel = disabled && selectedTagIds.length === 0;
    const { data, isLoading } = useControlProgramTagsQuery(initialTags);

    if (isLoading || !data) {
        return <EditableTagListInputSkeleton count={selectedTagIds.length} />;
    }
    if (showNoTagsLabel) {
        return (
            <Text type="secondary">{t("ControlProgram_Tags_NotAssigned")}</Text>
        );
    }
    return (
        <EditableControlProgramTagList
            selectedTagIds={selectedTagIds}
            cascaderClassName={cascaderClassName}
            canCreateTag={canCreateTag}
            disabled={disabled}
            inputRef={inputRef}
            onBlur={onBlur}
            tags={data.tags}
            tagCategories={data.tagCategories}
            setSelectedTagIds={setSelectedTagIds}
        />
    );
}

interface EditableControlProgramTagListProps {
    disabled: boolean;
    canCreateTag: boolean;
    setSelectedTagIds: (tagIds: Guid[]) => void;
    selectedTagIds: Guid[];
    tags: ControlProgramTag[];
    tagCategories: ControlProgramTagCategoryView[];
    cascaderClassName?: string;
    inputRef?: RefCallBack;
    onBlur?: () => void;
}
function EditableControlProgramTagList({
    disabled,
    canCreateTag,
    setSelectedTagIds,
    selectedTagIds,
    tags,
    tagCategories,
    cascaderClassName,
    onBlur,
    inputRef,
}: EditableControlProgramTagListProps) {
    const { mutateAsync: createTag } = useCreateControlProgramTagMutation();
    const handleCreateTag = async (tag: NewControlProgramTag) => {
        if (!canCreateTag) {
            throw new Error(
                "It should not be possible to call 'handleCreateTag' when 'canCreateTag' is 'false'"
            );
        }

        const response = await createTag({ data: tag });
        const newId = response.data.id;
        setSelectedTagIds([...selectedTagIds, newId]);
    };
    return (
        <EditableBaseTagListPlus<ControlProgramTag>
            onBlur={onBlur}
            inputRef={inputRef}
            allTagCategories={tagCategories}
            allTags={tags}
            isTagActive={isControlProgramTagActive}
            getTagCategoryId={getControlProgramTagCategoryId}
            disabled={disabled}
            canCreateTag={canCreateTag}
            selectedTagIds={selectedTagIds}
            cascaderClassName={cascaderClassName}
            onCreateTag={(name, categoryId) =>
                void handleCreateTag(
                    createNewControlProgramTag(name, categoryId)
                )
            }
            setSelectedTagIds={(tags) => void setSelectedTagIds(tags)}
        />
    );
}
